'use client';

import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import Button from '@wconcept/wconcept-ui-renewal/components/atoms/Button';
import {
  IconArrowH10,
  IconSelectCate2,
} from '@wconcept/wconcept-ui-renewal/components/atoms/svg';

import * as S from './style';

export interface IBreadcrumbMenuItem {
  /** 드롭다운 항목 */
  label?: React.ReactNode;
}

export interface IBreadcrumbItem {
  /** path 노출 텍스트 */
  title?: React.ReactNode;
  /** 하위 카테고리 드롭다운 */
  menu?: {
    items?: IBreadcrumbMenuItem[];
  };
}

interface IProps {
  /** breadcrumb 목록 */
  items?: IBreadcrumbItem[];
  /** className */
  className?: string;
}

const BreadcrumbDesktop = ({
  items,
  className,
}: IProps): React.ReactElement => {
  const breadcrumbClassName = classNameBind([
    'breadcrumb-desktop',
    className ?? '',
  ]);
  const [isOpen, setIsOpen] = useState(-1);
  const showAreaRef = useRef<HTMLOListElement>(null);

  // EVENT HANDLER : 외부 클릭시 닫기
  const clickOutside = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (showAreaRef.current && !showAreaRef.current.contains(target)) {
      setIsOpen(-1);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', clickOutside, true);
    return () => {
      document.removeEventListener('mousedown', clickOutside, true);
    };
  }, [clickOutside]);

  // EVENT HANDLER : 드롭다운 토글
  const onClickToggle = useCallback((key: number) => {
    setIsOpen(prev => (prev === key ? -1 : key));
  }, []);

  // ITEM RENDERER : 드롭다운 메뉴
  const getMenuRender = useCallback(
    (menuItems: IBreadcrumbMenuItem[]) => (
      <ul className="list-menu-dropdown">
        {menuItems.map((v, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={i} className="item-dropdown">
            {v.label}
          </li>
        ))}
      </ul>
    ),
    []
  );

  // ITEM RENDERER : breadcrumb 목록
  const getListRender = useCallback(
    (listItems: IBreadcrumbItem[]) => (
      <ol className="list" ref={showAreaRef}>
        {listItems.map((v, i) => {
          const hasMenu = (v.menu?.items?.length ?? 0) > 0;

          return (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i} className={`item${isOpen === i ? ' is-open' : ''}`}>
              <span className="icon">
                <IconArrowH10 />
              </span>
              <Button
                onClick={() => {
                  onClickToggle(i);
                }}
                ariaExpanded={hasMenu ? isOpen === i : undefined}
              >
                {v.title}
                {hasMenu && (
                  <span className="icon-cate">
                    <IconSelectCate2 />
                  </span>
                )}
              </Button>

              {hasMenu && getMenuRender(v.menu?.items ?? [])}
            </li>
          );
        })}
      </ol>
    ),
    [getMenuRender, isOpen, onClickToggle]
  );

  return (
    <S.BreadcrumbDesktop className={breadcrumbClassName}>
      {getListRender(items ?? [])}
    </S.BreadcrumbDesktop>
  );
};

export default memo(BreadcrumbDesktop);
